import { StackRankingLive, type StackRankingLiveItem } from './StackRankingLive';

export type SpeedBoatFactorType = 'engine' | 'anchor' | 'rock' | 'preserver';

export interface SpeedBoatLiveFactor {
  id: string;
  name: string;
  type: SpeedBoatFactorType;
}

interface SpeedBoatLiveProps {
  factors: SpeedBoatLiveFactor[];
  /** Ordered ids for engines (ranked). */
  engineRanking: string[];
  onEngineRankingChange: (ranking: string[]) => void;
  onTypeChange: (id: string, type: SpeedBoatFactorType) => void;
}

const TYPE_OPTS: { key: SpeedBoatFactorType; label: string }[] = [
  { key: 'engine', label: 'Wind (engine)' },
  { key: 'anchor', label: 'Anchor' },
  { key: 'rock', label: 'Rock' },
  { key: 'preserver', label: 'Preserver' },
];

export function SpeedBoatLive({
  factors,
  engineRanking,
  onEngineRankingChange,
  onTypeChange,
}: SpeedBoatLiveProps) {
  const engines = factors.filter(f => f.type === 'engine');
  const others = factors.filter(f => f.type !== 'engine');
  const engineItems: StackRankingLiveItem[] = engines.map(f => ({ id: f.id, name: f.name }));

  return (
    <div className="space-y-6">
      <div>
        <div className="font-heading text-[0.52rem] tracking-[0.12em] uppercase text-alpine-green-400 mb-2">
          Rank your engines (what moves you forward)
        </div>
        {engines.length > 0 ? (
          <StackRankingLive
            items={engineItems}
            ranking={engineRanking}
            onRankingChange={onEngineRankingChange}
          />
        ) : (
          <p className="font-body text-[0.78rem] text-[var(--text-ter)]">Mark items as engines below.</p>
        )}
      </div>

      <div>
        <div className="font-heading text-[0.52rem] tracking-[0.12em] uppercase text-[var(--text-ter)] mb-2">
          Classify each factor
        </div>
        <div className="space-y-2">
          {factors.map(f => (
            <div
              key={f.id}
              className="flex flex-col sm:flex-row sm:items-center gap-2 border border-[var(--rule-bright)] bg-[var(--surface)] px-3 py-2.5"
            >
              <span className="font-body text-[0.8rem] text-[var(--text-sec)] flex-1 min-w-0 truncate">{f.name}</span>
              <div className="flex flex-wrap gap-1">
                {TYPE_OPTS.map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => onTypeChange(f.id, opt.key)}
                    className={`font-heading text-[0.45rem] tracking-[0.06em] uppercase px-2 py-1 rounded border ${
                      f.type === opt.key
                        ? 'border-alpine-green-500/45 bg-alpine-green-500/15 text-alpine-green-400'
                        : 'border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--slate)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {others.length > 0 && (
        <p className="font-heading text-[0.48rem] tracking-[0.1em] uppercase text-[var(--text-dim)] text-center">
          {others.length} factor(s) classified — submit when engines are ranked
        </p>
      )}
    </div>
  );
}
